<template>
  <div class="main-wrapper">
    <layouts></layouts>
    <sidebar></sidebar>
    <div class="page-wrapper">
      <div class="content">
        <pageheader :title="title" :title1="title1" />

        <div class="card">
          <div class="card-body">
            <div v-if="loading" class="loading-wrap">
              <div class="loading-spinner"></div>
              <span>Loading purchase details...</span>
            </div>

            <template v-else>
              <div class="row mb-3">
                <div class="col-lg-3 col-sm-6 col-12">
                  <div class="form-group">
                    <label class="form-label">Supplier Name</label>
                    <b-form-select v-model="header.vendor" :options="vendorOptions" class="form-ctrl" />
                  </div>
                </div>
                <div class="col-lg-2 col-sm-6 col-12">
                  <div class="form-group">
                    <label class="form-label">Invoice No</label>
                    <b-form-input v-model="header.invoiceno" class="form-ctrl" :class="{ 'dup-invoice-input': invoiceDuplicate }" />
                    <div v-if="invoiceDuplicate" class="dup-invoice-warning" role="alert">
                    <i class="fas fa-exclamation-triangle"></i>
                    <span>{{ invoiceDuplicateText }} <a href="javascript:void(0);" @click="openDuplicatePurchase">View</a></span>
                  </div>
                  </div>
                </div>
                <div class="col-lg-2 col-sm-6 col-12">
                  <div class="form-group">
                    <label class="form-label">Invoice Date</label>
                    <b-form-input type="date" v-model="header.invoicedate" class="form-ctrl" />
                  </div>
                </div>
                <div class="col-lg-2 col-sm-6 col-12">
                  <div class="form-group">
                    <label class="form-label">Transport Mode</label>
                    <b-form-select v-model="header.transportmode" :options="transportModes" class="form-ctrl" />
                  </div>
                </div>
                <div class="col-lg-2 col-sm-6 col-12">
                  <div class="form-group">
                    <label class="form-label">Vehicle No</label>
                    <b-form-input v-model="header.vehicleno" placeholder="e.g. KL01AB1234" class="form-ctrl" />
                  </div>
                </div>
              </div>

              <div class="purchase-table-outer">
                <table class="purchase-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Item</th>
                      <th>Qty</th>
                      <th>Free</th>
                      <th>Purchase Price</th>
                      <th>Net PTR</th>
                      <th>MRP</th>
                      <th>Wholesale</th>
                      <th>Disc %</th>
                      <th>Tax %</th>
                      <th>Tax Amt</th>
                      <th>Batch</th>
                      <th>Mfg</th>
                      <th>Exp</th>
                      <th>Total</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(item, index) in items" :key="index">
                      <td>{{ index + 1 }}</td>
                      <td>
                        <div v-if="index < originalItemCount" class="readonly-item">{{ item.itemname }}</div>
                        <b-form-select
                          v-else
                          v-model="item.itemid"
                          :options="itemOptions"
                          class="pt-input"
                          @change="syncItemSelection(item)"
                        />
                      </td>
                      <td><b-form-input type="number" v-model.number="item.quantity" min="0" class="pt-input" @input="calcRow(item)" /></td>
                      <td><b-form-input type="number" v-model.number="item.freequantity" min="0" class="pt-input" @input="calcSummary" /></td>
                      <td><b-form-input type="number" v-model.number="item.purchaseprice" min="0" class="pt-input" @input="calcRow(item)" /></td>
                      <td><b-form-input :value="formatNumber(netPurchaseRate(item))" class="pt-input pt-input-ro" readonly /></td>
                      <td><b-form-input type="number" v-model.number="item.mrp" min="0" class="pt-input" /></td>
                      <td><b-form-input type="number" v-model.number="item.wholesaleprice" min="0" class="pt-input" /></td>
                      <td><b-form-input type="number" v-model.number="item.discount" min="0" max="100" class="pt-input" @input="calcRow(item)" /></td>
                      <td><b-form-input type="number" v-model.number="item.taxpercent" min="0" max="100" class="pt-input" @input="calcRow(item)" /></td>
                      <td><b-form-input type="number" v-model.number="item.taxamount" class="pt-input pt-input-ro" readonly /></td>
                      <td><b-form-input v-model="item.batchno" class="pt-input" /></td>
                      <td><b-form-input type="date" v-model="item.manufacturedate" class="pt-input" /></td>
                      <td><b-form-input type="date" v-model="item.expirydate" :min="todayStr" class="pt-input" /></td>
                      <td><b-form-input type="number" v-model.number="item.rowtotal" class="pt-input pt-input-ro" readonly /></td>
                      <td>
                        <button v-if="index >= originalItemCount" class="del-btn" @click="removeItem(index)">×</button>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div class="mt-2 mb-3">
                <span @click="addItem" class="add-item-btn">+ Add Item</span>
              </div>

              <div class="expense-card mb-3">
                <div class="expense-card-head">
                  <div>
                    <h4>Expense / Other Cost</h4>
                    <p>Add freight, loading, handling, or other bill-level cost.</p>
                  </div>
                  <span>Used in Net PTR</span>
                </div>
                <div class="expense-card-grid">
                  <div class="form-group mb-0">
                    <label class="form-label">Expense Type</label>
                    <b-form-select
                      v-model="header.purchase_expense.expense_type"
                      :options="purchaseExpenseTypeOptions"
                      class="form-ctrl"
                    />
                  </div>
                  <div class="form-group mb-0">
                    <label class="form-label">Amount</label>
                    <b-form-input
                      v-model.number="header.purchase_expense.amount"
                      type="number"
                      min="0"
                      step="0.01"
                      class="form-ctrl misc-input-wide"
                      @input="calcSummary"
                    />
                  </div>
                  <div class="form-group mb-0">
                    <label class="form-label">Remarks</label>
                    <b-form-textarea
                      v-model="header.purchase_expense.remarks"
                      rows="2"
                      placeholder="Freight, loading charge, supplier note..."
                      class="form-ctrl remarks-textarea"
                    />
                  </div>
                </div>
              </div>

              <div class="row justify-content-end mb-4">
                <div class="col-lg-4 col-md-6 col-sm-12">
                  <div class="totals-box">
                    <div class="totals-row">
                      <span>Total Amount</span>
                      <strong>₹ {{ formatNumber(summary.totalAmount) }}</strong>
                    </div>
                    <div class="totals-row">
                      <span>Tax Amount</span>
                      <strong>₹ {{ formatNumber(summary.taxAmount) }}</strong>
                    </div>
                    <div class="totals-row totals-net-row">
                      <span>Net Amount</span>
                      <strong>₹ {{ formatNumber(summary.netAmount) }}</strong>
                    </div>
                  </div>
                </div>
              </div>

              <div class="d-flex gap-2">
                <button @click="postData" class="btn-submit-custom" :disabled="submitting">
                  {{ submitting ? 'Updating...' : 'Update Purchase' }}
                </button>
                <router-link to="purchaselist" class="btn-cancel-custom">Cancel</router-link>
              </div>
            </template>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import Swal from "sweetalert2";
import { confirmIfDuplicateInvoice, duplicateInvoiceMessage, findDuplicateSupplierInvoice } from "@/services/purchaseInvoiceCheck";

axios.defaults.baseURL = process.env.VUE_APP_API_URL;
axios.defaults.headers.post["Content-Type"] = "application/json;charset=utf-8";
axios.defaults.headers.common["api-key"] = process.env.VUE_APP_API_KEY;

const emptyItem = () => ({
  itemid: "",
  itemname: "",
  quantity: 0,
  freequantity: 0,
  purchaseprice: 0,
  mrp: 0,
  wholesaleprice: 0,
  discount: 0,
  taxpercent: 0,
  cgst: 0,
  sgst: 0,
  taxamount: 0,
  batchno: "",
  expirydate: "",
  manufacturedate: "",
  rowtotal: 0,
});

export default {
  name: "editpurchase",
  data() {
    return {
      invoiceDuplicate: null,
      invoiceCheckTimer: null,
      title: "Edit Purchase",
      title1: "Update bill and all items",
      id: this.$route.query.id,
      loading: false,
      submitting: false,
      originalItemCount: 0,
      header: {
        vendor: "",
        billno: "",
        invoiceno: "",
        invoicedate: "",
        totalamount: 0,
        discountamount: 0,
        taxamount: 0,
        roundoff: 0,
        miscellaneous_amount: 0,
        purchase_expense: {
          expense_type: "FREIGHT",
          amount: 0,
          allocation_method: "VALUE",
          remarks: "",
        },
        netamount: 0,
        locationid: this.$store.getters.getCurrentLocation?.locationid || "",
        paymentmode: "Cash",
        transportmode: "",
        vehicleno: "",
        remarks: "",
      },
      transportModes: [
        { value: "",             text: "Select Mode" },
        { value: "Road",         text: "Road" },
        { value: "Rail",         text: "Rail" },
        { value: "Air",          text: "Air" },
        { value: "Ship",         text: "Ship" },
        { value: "Hand Delivery",text: "Hand Delivery" },
        { value: "Courier",      text: "Courier" },
        { value: "Other",        text: "Other" },
      ],
      purchaseExpenseTypeOptions: [
        { value: "FREIGHT", text: "Freight" },
        { value: "LOADING", text: "Loading" },
        { value: "HANDLING", text: "Handling" },
        { value: "PACKING", text: "Packing" },
        { value: "TRANSPORT", text: "Transport" },
        { value: "MISCELLANEOUS", text: "Miscellaneous" },
      ],
      items: [],
      summary: {
        totalAmount: 0,
        taxAmount: 0,
        netAmount: 0,
      },
      vendorListFromApi: [],
      itemListFromApi: [],
    };
  },
  computed: {
    invoiceDuplicateText() {
      return duplicateInvoiceMessage(this.invoiceDuplicate);
    },
    todayStr() {
      const t = new Date();
      const y = t.getFullYear();
      const m = String(t.getMonth() + 1).padStart(2, "0");
      const d = String(t.getDate()).padStart(2, "0");
      return `${y}-${m}-${d}`;
    },
    vendorOptions() {
      return [
        { value: "", text: "Select Supplier" },
        ...this.vendorListFromApi.map(v => ({ value: v.vendorid, text: v.vendorname })),
      ];
    },
    itemOptions() {
      return [
        { value: "", text: "Select Item" },
        ...this.itemListFromApi.map(i => ({ value: i.itemid, text: i.itemname })),
      ];
    },
    locationOptions() {
      return (this.$store.getters.getLocations || []).map((location) => ({
        value: location.locationid,
        text: location.locationname,
      }));
    },
  },
  watch: {
    "header.vendor"() {
      if (!this.loading) this.scheduleInvoiceCheck();
    },
    "header.invoiceno"() {
      if (!this.loading) this.scheduleInvoiceCheck();
    },
  },
  beforeMount() {
    this.id = this.$route.query.id;
    this.bootstrap();
  },
  methods: {
    scheduleInvoiceCheck() {
      clearTimeout(this.invoiceCheckTimer);
      this.invoiceCheckTimer = setTimeout(async () => {
        this.invoiceDuplicate = await findDuplicateSupplierInvoice(this.header.vendor, this.header.invoiceno, this.id);
      }, 400);
    },
    openDuplicatePurchase() {
      if (!this.invoiceDuplicate?.purchaseid) return;
      const route = this.$router.resolve({ path: "/purchase-details", query: { id: this.invoiceDuplicate.purchaseid } });
      window.open(route.href, "_blank", "noopener");
    },
    formatNumber(val) {
      return Number(val || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
    formatDateOnly(val) {
      if (!val) return "";
      const d = new Date(val);
      if (isNaN(d)) return "";
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, "0");
      const day = String(d.getDate()).padStart(2, "0");
      return `${y}-${m}-${day}`;
    },
    calcRow(item) {
      const qty = Number(item.quantity || 0);
      const price = Number(item.purchaseprice || 0);
      const discount = Number(item.discount || 0);
      const tax = Number(item.taxpercent || 0);
      const taxable = qty * price * (1 - discount / 100);
      const taxAmount = taxable * tax / 100;
      item.cgst = Number((taxAmount / 2).toFixed(2));
      item.sgst = Number((taxAmount / 2).toFixed(2));
      item.taxamount = Number(taxAmount.toFixed(2));
      item.rowtotal = Number((taxable + taxAmount).toFixed(2));
      this.calcSummary();
    },
    calcSummary() {
      const totalAmount = this.items.reduce((sum, item) => sum + Number(item.rowtotal || 0), 0);
      const taxAmount = this.items.reduce((sum, item) => sum + Number(item.taxamount || 0), 0);
      this.summary.totalAmount = Number(totalAmount.toFixed(2));
      this.summary.taxAmount = Number(taxAmount.toFixed(2));
      this.summary.netAmount = Number((
        totalAmount
        - Number(this.header.discountamount || 0)
        + Number(this.header.roundoff || 0)
        + this.purchaseExpenseAmount()
      ).toFixed(2));
      this.header.miscellaneous_amount = this.purchaseExpenseAmount();
      this.header.totalamount = this.summary.totalAmount;
      this.header.taxamount = this.summary.taxAmount;
      this.header.netamount = this.summary.netAmount;
    },
    linePurchaseBase(item) {
      return Number(item.quantity || 0) * Number(item.purchaseprice || 0);
    },
    lineStockQty(item) {
      return Number(item.quantity || 0) + Number(item.freequantity || 0);
    },
    netPurchaseRate(item) {
      const ptr = Number(item.purchaseprice || 0);
      const stockQty = this.lineStockQty(item);
      const totalBase = this.items.reduce((sum, row) => sum + this.linePurchaseBase(row), 0);
      const otherCost = this.purchaseExpenseAmount();
      const lineBase = this.linePurchaseBase(item);
      if (!stockQty || !totalBase || !otherCost) return ptr;
      const lineOtherCost = otherCost * (lineBase / totalBase);
      return Number(((lineBase + lineOtherCost) / stockQty).toFixed(2));
    },
    purchaseExpenseAmount() {
      return Number(this.header.purchase_expense?.amount || 0);
    },
    syncItemSelection(item) {
      const selected = this.itemListFromApi.find(opt => opt.itemid === item.itemid);
      item.itemname = selected?.itemname || "";
    },
    addItem() {
      this.items.push(emptyItem());
    },
    removeItem(index) {
      this.items.splice(index, 1);
      this.calcSummary();
    },
    mapItem(item) {
      const mapped = {
        ...emptyItem(),
        ...item,
        itemname: item.itemname || this.itemListFromApi.find(opt => opt.itemid === item.itemid)?.itemname || item.itemid,
        expirydate: this.formatDateOnly(item.expirydate),
        manufacturedate: this.formatDateOnly(item.manufacturedate),
      };

      const qty = Number(mapped.quantity || 0);
      const price = Number(mapped.purchaseprice || 0);
      const discount = Number(mapped.discount || 0);
      const taxable = qty * price * (1 - discount / 100);
      const savedTaxAmount = Number(mapped.taxamount || 0);

      mapped.taxamount = Number(savedTaxAmount.toFixed(2));
      mapped.cgst = Number((mapped.cgst || savedTaxAmount / 2).toFixed(2));
      mapped.sgst = Number((mapped.sgst || savedTaxAmount / 2).toFixed(2));
      mapped.rowtotal = Number((taxable + savedTaxAmount).toFixed(2));

      return mapped;
    },
    async fetchItemNamesForPurchase(items) {
      const missing = items
        .filter(item => !this.itemListFromApi.find(opt => opt.itemid === item.itemid))
        .map(item => item.itemid);

      await Promise.all([...new Set(missing)].map(async (itemid) => {
        try {
          const res = await axios.get(`/v1/items/${itemid}`);
          if (res.data?.itemid) this.itemListFromApi.push(res.data);
        } catch (error) {
          console.error(error);
        }
      }));
    },
    async bootstrap() {
      this.loading = true;
      try {
        const [vendorRes, itemRes, purchaseRes] = await Promise.all([
          axios.get("/v1/vendors"),
          axios.get("/v1/items"),
          axios.get(`/v1/purchases/${this.id}`),
        ]);

        this.vendorListFromApi = vendorRes.data || [];
        this.itemListFromApi = Array.isArray(itemRes.data) ? itemRes.data : (itemRes.data.items || []);

        const purchase = purchaseRes.data;
        await this.fetchItemNamesForPurchase(purchase.items || []);

        this.header = {
          vendor: purchase.vendor || "",
          billno: purchase.billno || "",
          invoiceno: purchase.invoiceno || "",
          invoicedate: this.formatDateOnly(purchase.invoicedate),
          totalamount: purchase.totalamount || 0,
          discountamount: purchase.discountamount || 0,
          taxamount: purchase.taxamount || 0,
          roundoff: purchase.roundoff || 0,
          miscellaneous_amount: purchase.miscellaneous_amount || 0,
          purchase_expense: {
            expense_type: purchase.purchase_expense?.expense_type || "FREIGHT",
            amount: purchase.purchase_expense?.amount ?? purchase.miscellaneous_amount ?? 0,
            allocation_method: purchase.purchase_expense?.allocation_method || "VALUE",
            remarks: purchase.purchase_expense?.remarks || "",
          },
          netamount: purchase.netamount || 0,
          locationid: purchase.locationid || this.$store.getters.getCurrentLocation?.locationid || "",
          paymentmode: purchase.paymentmode || "Cash",
          transportmode: purchase.transportmode || "",
          vehicleno: purchase.vehicleno || "",
          remarks: purchase.remarks || "",
        };

        this.items = (purchase.items || []).map(this.mapItem);
        if (
          this.items.length === 1 &&
          !Number(this.items[0].taxamount || 0) &&
          Number(purchase.taxamount || 0)
        ) {
          const headerTaxAmount = Number(purchase.taxamount || 0);
          this.items[0].taxamount = Number(headerTaxAmount.toFixed(2));
          this.items[0].cgst = Number((headerTaxAmount / 2).toFixed(2));
          this.items[0].sgst = Number((headerTaxAmount / 2).toFixed(2));
          this.items[0].rowtotal = Number((Number(this.items[0].rowtotal || 0) + headerTaxAmount).toFixed(2));
        }
        this.originalItemCount = this.items.length;
        this.calcSummary();
        if (!Number(this.summary.taxAmount || 0) && Number(purchase.taxamount || 0)) {
          this.summary.taxAmount = Number(Number(purchase.taxamount || 0).toFixed(2));
          this.header.taxamount = this.summary.taxAmount;
        }
      } catch (error) {
        console.error(error);
        Swal.fire({ icon: "error", title: "Error", text: "Failed to load purchase details." });
      } finally {
        this.loading = false;
      }
    },
    async postData() {
      if (!this.header.vendor) {
        Swal.fire({ icon: "warning", title: "Supplier Required", text: "Please select a supplier." });
        return;
      }
      if (this.items.some(item => !item.itemid)) {
        Swal.fire({ icon: "warning", title: "Item Required", text: "Please select an item for every row." });
        return;
      }
      if (this.items.some(item => Number(item.quantity || 0) <= 0)) {
        Swal.fire({ icon: "warning", title: "Invalid Quantity", text: "Quantity must be greater than 0." });
        return;
      }
      const purchaseExpenseAmount = this.purchaseExpenseAmount();
      if (purchaseExpenseAmount < 0) {
        Swal.fire({ icon: "warning", title: "Invalid Amount", text: "Purchase expense cannot be negative." });
        return;
      }
      if (purchaseExpenseAmount > 0 && !this.header.purchase_expense.expense_type) {
        Swal.fire({ icon: "warning", title: "Expense Type Required", text: "Please select an expense type." });
        return;
      }

      const result = await Swal.fire({
        title: "Update Purchase?",
        text: "This will update the full bill and all its items.",
        icon: "question",
        showCancelButton: true,
        confirmButtonColor: "#FF9F43",
        cancelButtonColor: "#d33",
        confirmButtonText: "Yes, update it!",
      });
      if (!result.isConfirmed) return;

      this.submitting = true;
      const payload = {
        vendor: this.header.vendor,
        billno: this.header.billno || null,
        invoiceno: this.header.invoiceno || null,
        invoicedate: this.header.invoicedate || null,
        transportmode: this.header.transportmode || null,
        vehicleno: this.header.vehicleno || null,
        paymentmode: this.header.paymentmode || null,
        locationid: this.header.locationid || null,
        remarks: this.header.remarks || null,
        totalamount: this.summary.totalAmount,
        discountamount: Number(this.header.discountamount || 0),
        taxamount: this.summary.taxAmount,
        roundoff: Number(this.header.roundoff || 0),
        miscellaneous_amount: purchaseExpenseAmount,
        purchase_expense: purchaseExpenseAmount > 0 ? {
          expense_type: this.header.purchase_expense.expense_type,
          amount: purchaseExpenseAmount,
          allocation_method: this.header.purchase_expense.allocation_method || "VALUE",
          remarks: this.header.purchase_expense.remarks || null,
        } : null,
        netamount: this.summary.netAmount,
        items: this.items.map(item => ({
          itemid: item.itemid,
          locationid: this.header.locationid || null,
          quantity: Number(item.quantity || 0),
          freequantity: Number(item.freequantity || 0),
          purchaseprice: Number(item.purchaseprice || 0),
          mrp: item.mrp || null,
          wholesaleprice: item.wholesaleprice || null,
          discount: item.discount || null,
          taxpercent: item.taxpercent || null,
          cgst: item.cgst || null,
          sgst: item.sgst || null,
          taxamount: item.taxamount || null,
          batchno: item.batchno || null,
          expirydate: item.expirydate || null,
          manufacturedate: item.manufacturedate || null,
        })),
      };

      if (!(await confirmIfDuplicateInvoice(this.header.vendor, this.header.invoiceno, this.id))) return;
      axios.put(`/v1/purchases/${this.id}`, payload)
        .then(() => {
          Swal.fire({ icon: "success", title: "Updated!", timer: 1500, showConfirmButton: false });
          this.$router.push({ path: "/purchaselist" });
        })
        .catch((error) => {
          console.error(error);
          Swal.fire({ icon: "error", title: "Update Failed", text: error.response?.data?.detail || "Something went wrong." });
        })
        .finally(() => {
          this.submitting = false;
        });
    },
  },
};
</script>

<style scoped>
.loading-wrap { display:flex; flex-direction:column; align-items:center; justify-content:center; padding:60px 20px; gap:14px; color:#6b7280; }
.loading-spinner { width:36px; height:36px; border:3px solid #f3f4f6; border-top-color:#fb923c; border-radius:50%; animation:spin .7s linear infinite; }
@keyframes spin { to { transform:rotate(360deg); } }
.form-label { font-size:.78rem; font-weight:600; color:#6b7280; margin-bottom:5px; display:block; }
.form-ctrl { height:36px; font-size:.875rem; border:1px solid #e5e7eb; border-radius:6px; padding:0 10px; }
.purchase-table-outer { overflow-x:auto; border:1px solid #e5e7eb; border-radius:8px; }
.purchase-table { width:100%; border-collapse:collapse; min-width:1480px; }
.purchase-table th { background:#f9fafb; font-size:.72rem; color:#6b7280; text-transform:uppercase; padding:9px 8px; border-bottom:1px solid #e5e7eb; }
.purchase-table td { padding:6px 8px; border-bottom:1px solid #f3f4f6; }
.pt-input { min-width:90px; height:30px; font-size:.8rem; }
.pt-input-ro { background:#f9fafb !important; }
.readonly-item { font-size:.82rem; font-weight:600; color:#111827; min-width:160px; }
.add-item-btn { display:inline-flex; align-items:center; gap:6px; padding:6px 14px; background:#fff7f0; color:#ea580c; border:1.5px dashed #fdba74; border-radius:6px; font-size:.85rem; font-weight:600; cursor:pointer; }
.expense-card { border:1px solid #e5e7eb; border-radius:8px; background:#fff; padding:14px; }
.expense-card-head { display:flex; justify-content:space-between; align-items:flex-start; gap:12px; margin-bottom:12px; }
.expense-card-head h4 { margin:0; font-size:.96rem; font-weight:700; color:#1f2937; }
.expense-card-head p { margin:4px 0 0; color:#6b7280; font-size:.8rem; }
.expense-card-head span { flex:0 0 auto; padding:5px 10px; border-radius:999px; border:1px solid #fed7aa; background:#fff7ed; color:#c2410c; font-size:.72rem; font-weight:700; }
.expense-card-grid { display:grid; grid-template-columns:180px 220px minmax(0, 1fr); gap:14px; align-items:start; }
.misc-input-wide { text-align:right; }
.remarks-textarea { min-height:70px; padding-top:8px; resize:vertical; }
.totals-box { border:1px solid #e5e7eb; border-radius:8px; overflow:hidden; background:#fff; }
.totals-row { display:flex; justify-content:space-between; align-items:center; padding:10px 14px; border-bottom:1px solid #f3f4f6; }
.totals-net-row { background:#fff7f0; color:#ea580c; font-weight:700; }
.misc-input { max-width:130px; height:32px; font-size:.85rem; text-align:right; }
.btn-submit-custom { padding:9px 24px; background:#ea580c; color:#fff; border:none; border-radius:7px; font-size:.875rem; font-weight:600; }
.btn-cancel-custom { display:inline-flex; align-items:center; padding:9px 20px; background:#f3f4f6; color:#374151; border:1px solid #e5e7eb; border-radius:7px; text-decoration:none; }
.del-btn { width:26px; height:26px; border:none; border-radius:50%; background:#fee2e2; color:#b91c1c; font-weight:700; }
@media (max-width: 991px) {
  .expense-card-grid { grid-template-columns:1fr; }
}
@media (max-width: 767px) {
  .expense-card-head { flex-direction:column; }
}

.dup-invoice-warning { display: flex; align-items: flex-start; gap: 6px; margin-top: 6px; padding: 6px 8px; border: 1px solid #fde68a; border-radius: 6px; background: #fffbeb; color: #92400e; font-size: 12px; line-height: 1.35; }
.dup-invoice-warning i { margin-top: 2px; }
.dup-invoice-warning a { color: #c2410c; font-weight: 600; text-decoration: underline; }
.dup-invoice-input { border-color: #f59e0b !important; }
</style>
